import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert, IconButton,
  CircularProgress, TableContainer, Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import api from '../../services/api';
import Layout from '../../components/Layout';

const statusColor = { open: 'warning', in_progress: 'info', resolved: 'success', wont_do: 'default' };
const catColor = { bug: 'error', feature: 'info', question: 'default' };
const catLabel = { bug: '🐛 Bug', feature: '✨ Feature', question: '❓ Question' };

export default function AdminFeedback() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/feedback?status=${filter}` : '/admin/feedback';
      const r = await api.get(url);
      setList(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError('Failed to load feedback'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({ status: item.status, admin_notes: item.admin_notes || '' });
  };

  const save = async () => {
    if (!editItem) return;
    setSaving(true); setError('');
    try {
      await api.put(`/admin/feedback/${editItem.id}`, editForm);
      setEditItem(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Feedback queue</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Bug reports, feature requests, and questions from your users.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by status</InputLabel>
            <Select value={filter} label="Filter by status" onChange={e => setFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="wont_do">Won't do</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <CircularProgress /> : list.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">No feedback matches this filter.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f6fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Screenshot</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell>
                      <Typography variant="caption">{new Date(f.created_at).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{f.user_email}</Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>{f.user_role}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={catColor[f.category] || 'default'} label={catLabel[f.category] || f.category} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.subject}</Typography>
                      <Typography variant="caption" sx={{ color: '#888', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{f.message}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor[f.status] || 'default'} label={f.status.replace('_',' ')} />
                    </TableCell>
                    <TableCell>
                      {f.screenshot_url ? (
                        <IconButton size="small" component="a" href={f.screenshot_url} target="_blank">
                          <ImageIcon fontSize="small" />
                        </IconButton>
                      ) : <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(f)}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={!!editItem} onClose={() => !saving && setEditItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e' }}>Triage feedback</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editItem && (
            <>
              <Box sx={{ bgcolor: '#f8f9ff', borderRadius: 2, p: 2, mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 700 }}>{catLabel[editItem.category]} · from {editItem.user_email}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, color: '#1a237e' }}>{editItem.subject}</Typography>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', color: '#333' }}>{editItem.message}</Typography>
                {editItem.page_path && <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', color: '#999' }}>Page: {editItem.page_path}</Typography>}
              </Box>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select value={editForm.status || ''} label="Status" onChange={e => setEditForm({...editForm, status: e.target.value})}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="wont_do">Won't do</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Internal notes" multiline rows={4} fullWidth size="small"
                value={editForm.admin_notes || ''}
                onChange={e => setEditForm({...editForm, admin_notes: e.target.value})}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditItem(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor: '#1a237e' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
